import React from 'react';

import { Box, ClassNameMap, GlobalStyles } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { Loader } from '../../Loader';
import { UpliftThemeProvider } from '../../theme';
import { StickyStackProvider } from '../StickyStack';
import { fullScreenLayoutBackgrounds } from './FullScreenLayout.component';
import { ScrollableView } from './ScrollableView.component';

type StyleProps = {
  background: keyof typeof fullScreenLayoutBackgrounds;
  classes?: ClassNameMap<'root' | 'content'>;
};


const useFullScreenWithoutBodyScrollLayoutStyles = makeStyles<StyleProps>()((_theme, props) => ({
  root: {
    height: '100%',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: fullScreenLayoutBackgrounds[props.background],
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  }
}));


export type FullScreenWithoutBodyScrollLayoutProps = {
  background?: keyof typeof fullScreenLayoutBackgrounds;
  classes?: ClassNameMap<'root' | 'content'>;
  loading?: boolean;
  navigation?: React.ReactElement;
};


export const FullScreenWithoutBodyScrollLayout: React.FC<FullScreenWithoutBodyScrollLayoutProps> = ({
  classes: _classes,
  background = 'grey',
  navigation = null,
  loading,
  children,
}) => {
  const { classes } = useFullScreenWithoutBodyScrollLayoutStyles({
    background
  }, {
    props: {
      classes: _classes
    }
  });

  const loader = <Loader color="primary" height="100%" />;

  return (
    <UpliftThemeProvider>
      <GlobalStyles
        styles={{
          'html, body': {
            height: '100%',
            minHeight: '-webkit-fill-available',
            overflow: 'hidden',
            WebkitOverflowScrolling: 'touch',
          }
        }}
      />
      <StickyStackProvider>
        <Box className={classes.root}>
          {navigation}
          <Box display="flex" flexDirection="column" flex="1" width="100%">
            <ScrollableView>
              <Box component="main" id="provider-layout" className={classes.content}>
                <React.Suspense fallback={loader}>
                  {loading ? loader : children}
                </React.Suspense>
              </Box>
            </ScrollableView>
          </Box>
        </Box>
      </StickyStackProvider>
    </UpliftThemeProvider>
  );
};
